import { Observable, defer } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { IPaginatedResult, ISapDataService, ISort, ODataFilter } from 'src/app/lego-utils';
import { ErrorState } from './error-state';
import { ItemUiState } from './item-ui-state';
import { IPage } from './paged-collection';

export interface IItemUi {
  error?: string;
  isLoading: boolean;
}

/**
 * The SAP CRUD surface `bom-item` and `task` actually use, over a plain
 * `ISapDataService`. Replaces `PagedSapEntityService`, which wrapped Akita's
 * `EntityStore` + `NgEntityServiceLoader` and wrote every result into a normalized
 * store that only the paginator read back. See STEP-4D-PLAN.md.
 *
 * Dropped from the original: `getMultiple` (both domains call the data service
 * directly), the `skipWrite` config — always `true` at every call site, since nothing
 * reads the store — and the `NgEntityServiceLoader` dispatches, whose only consumer
 * was one drawer spinner.
 */
export abstract class PagedSapService<
  T extends { id: string },
  TUpdate extends Partial<T> = Partial<T>,
  TDeleteOptions = unknown,
> {
  protected readonly state = new ErrorState();
  protected readonly uiState = new ItemUiState<IItemUi>();

  public readonly error$ = this.state.error$;
  public readonly uiById$ = this.uiState.byId$;

  protected constructor(
    protected readonly dataService: ISapDataService<T, string, TUpdate, TDeleteOptions>
  ) {}

  /**
   * No `expand` parameter: both data services ignore it (task's is literally named
   * `_expand`), and the old base always passed `[createExpands(undefined)]` — i.e.
   * `['']` — because no caller supplied expands either.
   */
  public getPage(
    page: number,
    perPage: number,
    sort?: Array<ISort<T>>,
    filters?: ODataFilter
  ): Observable<IPage<T>> {
    return defer(() => {
      this.state.setError(undefined);

      return this.dataService.getPage(page, perPage, sort, filters).pipe(
        // currentPage and perPage come from the *result*, as the old base did by
        // spreading it — PagedCollection keys its cache on currentPage, so taking the
        // requested page instead would cache a response under the wrong key whenever
        // the service echoes something different.
        map((result: IPaginatedResult<T>): IPage<T> => ({
          ...result,
          lastPage: Math.ceil(result.total / result.perPage),
        })),
        tap({
          error: (error: Error) => {
            this.state.setError(error.message);
          },
        })
      );
    });
  }

  public getSingle(id: string): Observable<T> {
    return this.trackItem(id, () => this.dataService.getSingle(id));
  }

  public update(id: string, entity: TUpdate): Observable<T> {
    return this.trackItem(id, () => this.dataService.update(id, entity));
  }

  public add(entity: Partial<T>): Observable<T> {
    return defer(() => this.dataService.create(entity));
  }

  public delete(id: string, options?: TDeleteOptions): Observable<void> {
    return this.trackItem(id, () => this.dataService.delete(id, options));
  }

  /**
   * Per-item loading/error, which the old base kept in an `EntityUIStore`. Errors are
   * recorded rather than swallowed — every caller also handles them, but material-bom's
   * template renders `uiState[id].error` beside the row.
   */
  private trackItem<R>(id: string, request: () => Observable<R>): Observable<R> {
    return defer(() => {
      this.uiState.set(id, { error: undefined, isLoading: true });

      return request().pipe(
        tap({
          error: (error: unknown) => {
            this.uiState.set(id, {
              error: error instanceof Error ? error.message : undefined,
              isLoading: false,
            });
          },
        }),
        finalize(() => {
          this.uiState.patch([id], { isLoading: false });
        })
      );
    });
  }
}
